import React, {useEffect, useState, useCallback} from 'react'
import { Box, Tab, Tabs, useMediaQuery, useTheme} from '@mui/material'
import MpTheme from '../../../config/MpTheme'
import { UserDetailsTab } from './Tabs/UserDetailsTab'
import { CorpTab } from './Tabs/CorpTab'
import { RemovalsTab } from './Tabs/RemovalsTab'
import { SubscriptionsTab } from './Tabs/SubscriptionsTab'
import { FamilyTab } from './Tabs/FamilyTab'
import { useAppDispatch } from '../../../redux/storeExports'
import { createLookerReportLastSyncApi } from '../../../redux/corporateCodeService'
import { lastSyncFormattedInitialState} from './Types'
import { formatLastSyncTime, getCachedLastSync, setCachedLastSync, shouldRefetch, LastSyncItem } from '../../../utils/helpers'
import { IndividualUserTab } from './Tabs/IndividualUserTab'
import ExportReportsTab from './Tabs/ExportLookerReportTab'

type TabType=
|'User'
|'Company'
|'Removals'
|'Subscriptions'
|'Family'
|'Individual'
|'Export'

interface TabPanelProps {
    children?: React.ReactNode
    tabValue: TabType
}

    const UserReports=()=>{
        const theme = useTheme()
        const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
        const [activeTabValue, setActiveTabValue] = useState<TabType>('User')
        const [lastSyncTime, setLastSyncTime] = useState(lastSyncFormattedInitialState)

        const handleTabChange = (event: React.SyntheticEvent, tab: TabType)=>{
            setActiveTabValue(tab)
        }

        const dispatch = useAppDispatch()

        const getLastSync = useCallback(async () => {
        const cached = getCachedLastSync();

        if (cached && !shouldRefetch(cached)) {
            setLastSyncTime(formatLastSyncTime(cached));
            return;
        }

        const response: LastSyncItem[] = await dispatch(createLookerReportLastSyncApi()).unwrap();
        setCachedLastSync(response);
        setLastSyncTime(formatLastSyncTime(response));
        }, [dispatch, setLastSyncTime]);

        useEffect(() => {
        getLastSync();
    }, [getLastSync]);


    const getTabLabelStyle = (tabValue : TabType) =>{
        return {
            fontWeight: activeTabValue === tabValue ? 700 : 500,
            textTransform: 'none',
            fontSize: '1rem',
            color:
            activeTabValue === tabValue
            ? MpTheme.colors.primary.default
            : MpTheme.colors.gray.default,
            padding: '12px 24px',
            borderRadius: '8px 8px 0 0',
            '&:hover': {
            backgroundColor:
            activeTabValue === tabValue ? 'transparent' : 'rgba(0, 0, 0, 0.03)',
            color:activeTabValue === tabValue
            ? MpTheme.colors.primary.default
            : MpTheme.colors.primary[300],
            },
        }
    }
    
    
    const TabPanel=(props: TabPanelProps)=>{
      const { children, tabValue} = props
      return (
        <div
          role="tabpanel"
          hidden={activeTabValue !== tabValue}
          id={`user-records-tabpanel-${tabValue}`}
          >
          {tabValue === activeTabValue && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
      )
    }
    
    return(
    <Box sx={{width:'100%'}}>
        <Box>
            <Tabs
                value={activeTabValue}
                onChange={handleTabChange}
                indicatorColor="secondary"
                textColor="primary"
                scrollButtons={false}
                allowScrollButtonsMobile
                orientation={isMobile ? 'vertical' : 'horizontal'}
                variant={isMobile ? 'standard' : 'scrollable'}
                sx={{
                        flexWrap: 'wrap',
                        '& .MuiTabs-flexContainer': {
                            flexWrap: isMobile ? 'wrap' : 'nowrap',
                            justifyContent: 'space-around',
                        },
                        '& .MuiTabs-indicator': {
                            height: 3,
                            borderRadius: '3px 3px 0 0',
                            backgroundColor: MpTheme.colors.primary.default,
                        },
                        '& .MuiTab-root': {
                            transition: 'all 0.2s ease',
                            mx: 1,
                        }
                    }}
            >
                 <Tab
                    label="All Users Report"
                    value={'User'}
                    sx={getTabLabelStyle('User')}
                />
                <Tab
                    label="Removals Report"
                    value={'Removals'}
                    sx={getTabLabelStyle('Removals')}
                />
                 <Tab
                    label="Subscription Report"
                    value={'Subscriptions'}
                    sx={getTabLabelStyle('Subscriptions')}
                />
                <Tab
                    label="Company Report"
                    value={'Company'}
                    sx={getTabLabelStyle('Company')}
                />
                <Tab
                    label="Family Report"
                    value={'Family'}
                    sx={getTabLabelStyle('Family')}
                />
                <Tab
                    label="Individual User Report"
                    value={'Individual'}
                    sx={getTabLabelStyle('Individual')}
                />
                 <Tab
                    label="Export Report"
                    value={'Export'}
                    sx={getTabLabelStyle('Export')}
                />
            </Tabs>
        </Box>
        <TabPanel tabValue='User'>
            <UserDetailsTab userReportSyncTime={lastSyncTime["users_report.parquet"]}/>  
        </TabPanel>
        <TabPanel tabValue='Removals'>
            <RemovalsTab removalReportSyncTime={lastSyncTime['removals_report.parquet']}/>
        </TabPanel>
        <TabPanel tabValue='Subscriptions'>
            <SubscriptionsTab subscriptionReportSyncTime={lastSyncTime['subscription_reporting_stats.parquet']} AccountSubscriptionReportSyncTime={lastSyncTime['account_subscription_creation_stats.parquet']}/>
        </TabPanel>
        <TabPanel tabValue='Company'>
            <CorpTab corpUsersReportSyncTime={lastSyncTime['corp_users_report.parquet']} companyReportSyncTime={lastSyncTime['company_report.parquet']}/>
        </TabPanel>
        <TabPanel tabValue='Family'>
            <FamilyTab familyReportSyncTime={lastSyncTime['family_report.parquet']}/>
        </TabPanel>
        <TabPanel tabValue='Individual'>
            <IndividualUserTab IndividualUserReportSyncTime={lastSyncTime['individual_user_report.parquet']}/>
        </TabPanel>
        <TabPanel tabValue='Export'>
            <ExportReportsTab/>  
        </TabPanel>
    </Box>
)
}

export default UserReports
